import React from "react";
import { GoArrowLeft, GoArrowRight } from "react-icons/go";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  btnClass?: string;
  pageNumClass?: string;
  activePageNumClass?: string;
  inactivePageNumClass?: string;
  activePageNumStyle?: React.CSSProperties;
  /** Max number of page number buttons to show (sliding window). Default 7. */
  maxVisiblePages?: number;
}

/**
 * Returns an array of page numbers to display, with null representing an ellipsis.
 * e.g. [1, null, 4, 5, 6, null, 10]
 */
function getVisiblePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number
): (number | null)[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, currentPage - half);
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages: (number | null)[] = [];
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push(null);
  }
  for (let p = start; p <= end; p++) {
    pages.push(p);
  }
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(null);
    pages.push(totalPages);
  }
  return pages;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  btnClass,
  pageNumClass,
  activePageNumClass,
  inactivePageNumClass,
  activePageNumStyle,
  maxVisiblePages = 7,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const visiblePages = getVisiblePageNumbers(
    currentPage,
    totalPages,
    maxVisiblePages
  );

  const baseBtnClass = "inline-flex items-center justify-center gap-2";
  const defaultBtnClass =
    "px-2 py-1 border rounded hover:bg-[#f4e4c9] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent";
  const prevNextClass = `${baseBtnClass} ${btnClass ?? defaultBtnClass}`;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={() => goToPage(currentPage - 1)}
        className={prevNextClass}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <GoArrowLeft size={16} className="shrink-0" />
        <span>Prev</span>
      </button>

      <div className="flex items-center gap-1 flex-nowrap">
        {visiblePages.map((page, idx) =>
          page === null ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-1 text-gray-400 select-none"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={
                pageNumClass && activePageNumClass && inactivePageNumClass
                  ? `${pageNumClass} ${page === currentPage ? activePageNumClass : inactivePageNumClass}`
                  : `px-2 py-1 border rounded text-sm ${page === currentPage ? "pagination-selected-color text-black" : "hover:bg-[#f4e4c9] hover:text-black"}`
              }
              style={
                page === currentPage && activePageNumStyle
                  ? activePageNumStyle
                  : undefined
              }
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => goToPage(currentPage + 1)}
        className={prevNextClass}
        disabled={currentPage === totalPages || totalPages === 0}
        aria-label="Next page"
      >
        <span>Next</span>
        <GoArrowRight size={16} className="shrink-0" />
      </button>
    </div>
  );
};

export default Pagination;
